import { TrialOfferUnit } from "@/types";
import { REGEX } from "@/utils";
import {
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { FC, useState } from "react";
import { UpdateTrialOfferProps } from "./update-trial-offer.types";
import { useGetAffiliateUser, useUpdateTrialOffer } from "../../queries";

const UpdateTrialOffer: FC<UpdateTrialOfferProps> = ({
  affiliateId,
  trialOfferUnitPrev,
  trialOfferAmountPrev,
}) => {
  const [includesTrialOffer, setIncludesTrialOffer] = useState(
    !!(trialOfferAmountPrev || trialOfferUnitPrev) || false
  );
  const [trialOfferAmount, setTrialOfferAmount] = useState(
    trialOfferAmountPrev ? String(trialOfferAmountPrev) : ""
  );
  const [trialOfferUnit, setTrialOfferUnit] = useState<TrialOfferUnit | "">(
    trialOfferUnitPrev || ""
  );
  const [touched, setTouched] = useState({ amount: false, unit: false });

  const [errors, setErrors] = useState<{ amount?: string; unit?: string }>({});

  const { affiliate } = useGetAffiliateUser(affiliateId);

  const { updateTrialOffer, isUpdateTrialOfferLoading } = useUpdateTrialOffer({
    onSuccess: ({ trialOfferAmount, trialOfferUnit }) => {
      setTrialOfferUnit(trialOfferUnit || "");
      setTrialOfferAmount(trialOfferAmount ? String(trialOfferAmount) : "");

      setTouched({ amount: false, unit: false });
      setErrors({});
    },
  });

  const validate = () => {
    const newErrors: typeof errors = {};

    if (includesTrialOffer) {
      if (!trialOfferAmount) {
        newErrors.amount = "Required";
      } else if (!REGEX.ONLY_NUMBERS.test(trialOfferAmount)) {
        newErrors.amount = "Must be a number";
      }

      if (!trialOfferUnit) {
        newErrors.unit = "Required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = () => {
    if (!affiliate?.id) return;

    if (includesTrialOffer) {
      return updateTrialOffer({
        affiliateId: affiliate.id,
        trialOfferAmount: +trialOfferAmount,
        trialOfferUnit: trialOfferUnit as TrialOfferUnit,
      });
    }
    updateTrialOffer({
      affiliateId: affiliate.id,
      trialOfferAmount: null,
      trialOfferUnit: null,
    });
  };

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Divider
          sx={{
            borderColor: "#333",
            width: "calc(100% - 56px)",
          }}
        >
          Trial Offer
        </Divider>
        <FormControlLabel
          label=""
          labelPlacement="start"
          control={
            <Checkbox
              checked={includesTrialOffer}
              onChange={(e) => {
                setIncludesTrialOffer(e.target.checked);
                setTouched({ amount: false, unit: false });
              }}
            />
          }
        />
      </Stack>

      <FormControl
        fullWidth
        disabled={!includesTrialOffer}
        error={touched.unit && !!errors.unit}
      >
        <InputLabel>Period</InputLabel>
        <Select
          value={trialOfferUnit}
          label="Trial Period"
          onChange={(e) => setTrialOfferUnit(e.target.value as TrialOfferUnit)}
          onBlur={() => {
            setTouched((prev) => ({ ...prev, unit: true }));
            validate();
          }}
        >
          <MenuItem value="days">Days</MenuItem>
          <MenuItem value="months">Months</MenuItem>
        </Select>
      </FormControl>

      <TextField
        fullWidth
        disabled={!includesTrialOffer}
        value={trialOfferAmount}
        placeholder="Amount of period"
        variant="outlined"
        label={
          touched.amount && errors.amount ? errors.amount : "Amount of period"
        }
        error={touched.amount && !!errors.amount}
        onBlur={() => {
          setTouched((prev) => ({ ...prev, amount: true }));
          validate();
        }}
        onChange={(e) => {
          const value = e.target.value;
          if (!REGEX.ONLY_NUMBERS.test(value) && value !== "") return;
          setTrialOfferAmount(value);
        }}
      />

      <Button
        sx={{ width: 156 }}
        loading={isUpdateTrialOfferLoading}
        disabled={
          (includesTrialOffer
            ? !(trialOfferAmount && trialOfferUnit)
            : false) ||
          isUpdateTrialOfferLoading ||
          !affiliate?.id
        }
        onClick={onSubmit}
      >
        Save
      </Button>
    </Stack>
  );
};

export { UpdateTrialOffer };
